/* eslint-disable unicorn/prefer-blob-reading-methods */
import _ from 'lodash-es';
import Query from '~/scripts/services/model/query';
import Defaults from '~/scripts/constants/defaults';
import { Confirm } from '~/scripts/services/modal/modal';
import Node from '~/scripts/services/model/node';
import APIV5 from '~/models/api';
import { COMPOSED_OF } from '~/scripts/constants/common-kids';
import QueryColumn from '~/scripts/services/model/query-column';
import Utils from '~/scripts/services/utils';

export default class QueryTab {
	/**
	 * @param {import('angular').auto.IInjectorService} $injector
	 */
	/* @ngInject */
	constructor($injector) {
		this.$injector = $injector;
		this.$q = $injector.get('$q');
		this.$timeout = $injector.get('$timeout');
		this.$translate = $injector.get('$translate');
		this.NodeService = $injector.get('NodeService');
		this.Notification = $injector.get('Notification');
		this.User = $injector.get('User');

		/** @type {boolean} */
		this.create = false;

		/** @type {boolean} */
		this.new = true;

		/** @type {boolean} */
		this.edit = true;

		/** @type {?import('./query-editor/query-editor.controller').default} */
		this.editorController = null;

		/** @type {?import('angular').IFormController} */
		this.editorForm = null;

		/** @type {any} */
		this.isaLangs = {};

		/** @type {boolean} */
		this.loading = false;

		/** @type {string} */
		this.message = this.$translate.instant('list.no-results');

		/** @type {string} */
		this.name = '';

		/** @type {Node} */
		this.node = new Node(this.$injector);

		/** @type {number} */
		this.packet_index = 1;

		/** @type {number} */
		this.packet_size = 50;

		/** @type {Query} */
		this.query = new Query();

		this.result = {
			/** @type {any} */
			class:        {},

			/** @type {?number} */
			duration:     null,

			/** @type {any} */
			lang:         {},

			/** @type {?number} */
			packet_index: null,

			/** @type {?number} */
			packet_size:  null,

			/** @type {any[]} */
			rows:         [],

			/** @type {number} */
			total:        0,
		};

		/** @type {any} */
		this.variable = {};

		/** @type {boolean} */
		this.visible = true;
	}

	/**
	 * Adds a column
	 *
	 * @param {any} item columns details
	 * @returns {?QueryColumn}
	 */
	addSelect(item) {
		if ('condition' in item || 'op' in item) {
			return null;
		}

		const select = new QueryColumn({
			class_kid:         item.class_kid,
			inherits_extended: item.inherits_extended ?? -1,
			kdata:             {},
			kid:               item.node,
			linker_kid:        item.linker_kid,
			linker_info:       item.linker_info,
			name:              item.name,
			node:              'REPRE',
			op:                'detail',
			path:              item.path,
			type:              item.type,
		});
		const separator = item.flow === 'up' ? '◀' : '▶';

		if ('from' in item) {
			// TODO: check why we need this??
			// @ts-ignore
			select.from = item.from;
		}

		// Vérif nom en double dans les autres colonnes
		for (const [ks, s] of this.query.select.entries()) {
			if (
				s.name === select.name
				&& s.linker_kid === select.linker_kid
				&& s.path?.length > 1
				&& select.path?.length > 1
				&& !s.name.includes('▶')
				&& !s.name.includes('◀')
				&& !_.isEqual(s.path, select.path)
			) {
				this.query.select[ks].name = `${s.path.at(-2).name} ${separator} ${s.name}`;
				select.name = `${select.path.at(-2).name} ${separator} ${select.name}`;
			}
		}

		return select;
	}

	/**
	 * Closes query
	 *
	 * @returns {Promise<any>} Promise result
	 */
	async close() {
		const deferred = this.$q.defer();

		if (this.editorForm?.$dirty) {
			const modal = new Confirm(this.$injector);
			const answer = await modal.open({
				title: 'Confirmation de fermeture',
				body: '<b>Cette requête n\'a pas été sauvegardée !</b>',
				accept: {
					label: 'Ignorer les changements',
					type: 'danger',
				},
			});

			if (answer) {
				return deferred.resolve();
			}
		}
		else {
			deferred.resolve();
		}

		return deferred.promise;
	}

	/**
	 * @param {import('@knitiv/api-client-javascript').KNode | string} elementKid
	 * @returns {Promise<QueryTab>} QueryTab instance
	 */
	async createTab(elementKid) {
		const user = this.User.getProfil();

		this.create = true;
		this.node = this.NodeService.create('K_NODE;KSYSQUERY', elementKid);
		this.node.setName(this.$translate.instant('query.new-query'));
		this.node.name = this.node.getName();
		await this.node.addDroit({
			kright: 15,
			role: {
				kid:  user.kid,
				name: user.name,
			},
		});
		return this.$q.resolve(this);
	}

	export() {
		if (this.node) {
			Utils.download(
				JSON.stringify(this.query.export()),
				'application/json',
				`${this.node.getName() || 'query'}.json`,
			);
		}
	}

	// noinspection JSUnusedGlobalSymbols
	/**
	 * @param {QueryColumn} select
	 */
	async extendTypeNodes(select) {
		if (select.repre === undefined) {
			select.repre = {};
		}

		if (select.kid) {
			if (!(select.kid in this.isaLangs)) {
				/** @type {Node} */
				const node = await this.NodeService.load(select.kid);
				this.isaLangs[select.kid] = {};
				for (const lang of node.isa_lang) {
					this.isaLangs[select.kid][lang.kid] = {
						name:  lang.name,
						group: this.$translate.instant('query.select.node-group.repre'),
					};
				}

				if (node.isfile) {
					for (const attribute of Defaults.query.selectTypeNodesAttributes) {
						this.isaLangs[select.kid][attribute] = {
							name:  this.$translate.instant(`query.select.node-attribute.${attribute}`),
							group: this.$translate.instant('query.select.node-group.attr'),
						};
					}
				}
			}

			this.$timeout(() => {
				if (select.kid) {
					_.merge(select.repre, this.isaLangs[select.kid]);
				}
			});
		}
	}

	// eslint-disable-next-line class-methods-use-this
	/**
	 * @param {QueryColumn} select
	 * @returns {any} Path item
	 */
	getSelectLastPath(select) {
		if (!select) {
			return select;
		}

		const { path } = select;

		return path ? path.length > 0 && path.at(-1) : select;
	}

	// noinspection JSUnusedGlobalSymbols
	// eslint-disable-next-line class-methods-use-this
	/**
	 * @param {QueryColumn} select
	 */
	getTypesNode(select) {
		if (!select) {
			return;
		}

		if (select.repre === undefined) {
			select.repre = {};
		}

		for (const typeNode of Defaults.query.selectTypeNodes) {
			select.repre[typeNode.id] = _.merge({}, typeNode, {
				name: this.$translate.instant(`query.select.node.${typeNode.id}`),
				group: this.$translate.instant(`query.select.node-group.${typeNode.group}`),
			});
		}

		for (const typeNodeAttribute of Defaults.query.selectTypeNodesAttributes) {
			select.repre[`KATTR:${typeNodeAttribute}`] = _.merge({}, {
				name: this.$translate.instant(`query.select.node-attribute.${typeNodeAttribute}`),
				group: this.$translate.instant('query.select.node-group.attr'),
			});
		}

		select.repre['KDROIT:_ALL_'] = {
			name: this.$translate.instant('common.all'),
			group: this.$translate.instant('common.rights'),
		};

		if (!(select.node in select.repre)) {
			this.extendTypeNodes(select);
		}
	}

	/**
	 * Import a JSON query
	 *
	 * @param {File} file File to import
	 */
	import(file) {
		const fr = new FileReader();
		// const user = this.User.getProfil();

		fr.addEventListener('load', async () => {
			let rawQuery;

			try {
				// @ts-ignore
				rawQuery = JSON.parse(fr.result.toString());
			}
			catch {
				this.Notification.error({
					title:   this.$translate.instant('error.title'),
					message: this.$translate.instant('error.no-json-file'),
				});
			}

			this.create = true;
			this.new = false;
			this.edit = true;
			this.visible = true;
			this.query = new Query(rawQuery);
			await this.query.fixMissingNames();
			this.$timeout(() => this.editorController?.initColumns());
		});
		if (file !== undefined) {
			fr.readAsText(file);
		}
	}

	/**
	 * @param {string} queryId
	 * @param {boolean} edit
	 * @param {boolean} duplicate
	 * @returns {Promise<Node>} Query Node instance
	 */
	async load(queryId, edit, duplicate) {
		const node = await this.NodeService.load(queryId);
		const attributeQuery = node?.getAttribute('QUERY');
		if (attributeQuery) {
			this.new = false;
			this.edit = edit;
			this.node = node;
			this.query = new Query(attributeQuery.value);

			const linkVisible = this.node.link.find(link => link.linker_kid === 'K_NODE;VISIBLE');
			this.visible = linkVisible ? linkVisible.objnum2_kid !== 'K_NODE;FALSE' : true;

			if (duplicate) {
				this.create = true;
				this.node.name = `${node.getName()} (${this.$translate.instant('common.copy')})`;
				this.node.setName(this.node.name);

				this.node.kid = /** @type {import('@knitiv/api-client-javascript').KNode} */ `K_NODE;${this.User.getRef()}`;
			}
		}

		return node;
	}

	/**
	 * Saves the query
	 *
	 * @returns {Promise<any>} result
	 */
	async save() {
		if (this.create) {
			this.node.addAttribute({
				name:     'QUERY',
				value:    this.query.export(),
				ref_type: 'KEPLER_TYPE;JSON',
			});
		}
		else {
			const attribute = this.node.getAttribute('QUERY');
			if (attribute) {
				attribute.value = this.query.export();
				this.node.modAttribute(attribute);
			}
		}

		if (this.node.kid) {
			const objectList = {
				[this.node.kid]: {
					ATTRIBUT_LIST:  this.node.attribut.map(attribut => attribut.export()),
					DROITCUST_LIST: this.node.droitcust.map(droitcust => droitcust.export()),
					DROITDEF_LIST:  this.node.droitdef.map(droitdef => droitdef.export()),
					REPRE_LIST:     this.node.repre.map(repre => repre.exportForObjectSet()),
					LINK_LIST:      {
						[COMPOSED_OF]: [
							{
								class_kid:    'K_NODE;BOOL',
								linker_kid:   'K_NODE;VISIBLE',
								objnum2:      this.visible ? 'K_NODE;TRUE' : 'K_NODE;FALSE',
								objnum2_isa:  'K_NODE;BOOL',
								relation_kid: COMPOSED_OF,
							},
						],
					},
					isa:            this.node.isa.objid,
				},
			};

			await APIV5.instance.objectSet({
				items: [
					{
						target_objnum: this.node.kid,
						// @ts-ignore
						OBJECT_LIST:   objectList,
					},
				],
			}, { id: 'query.save' });
			this.create = false;
			this.$injector.get('$rootScope').$broadcast('Query.refreshList');
		}
	}
}
